"use client";

import { useState } from "react";
import { useAppSelector } from "@/hooks/hooks";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import Link from "next/link";
import { ChevronLeft, ChevronRight, MapPin } from "lucide-react";
import { insertBookingReference } from "@/services/resort.services";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation } from "swiper/modules";
import { useRouter } from "next/navigation";
import dynamic from "next/dynamic";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

const InquiryDialog = dynamic(() => import("./InquiryDialog"), { ssr: false });

interface Package {
  ID: number;
  Title: string;
  URL: string;
  ResortID: number;
  Price: number;
  PricePer: string;
  MainImage: string;
  BannerImage: string;
  BookingURL?: string;
  IsActive: boolean;
}

interface Resort {
  ID: number;
  Title: string;
  ShortDescription?: string;
  TentCityID?: number;
  TentCityName?: string;
  URL: string;
  Price?: number;
  ResortWisePackage?: Package[];
}

function ResortSliderSection({ resort }: { resort: Resort }) {
  const packages = resort.ResortWisePackage || [];

  const [prevEl, setPrevEl] = useState<HTMLButtonElement | null>(null);
  const [nextEl, setNextEl] = useState<HTMLButtonElement | null>(null);
  const [paginationEl, setPaginationEl] = useState<HTMLDivElement | null>(null);

  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState<any>(null);

  const handleInquiryOpen = (pkg: any) => {
    setSelectedPkg(pkg);
    setInquiryOpen(true);
  };

  const handleBookNow = (pkg: Package) => {
    insertBookingReference(0, window.location.href).catch(() => {});
    if (pkg.BookingURL) {
      window.open(pkg.BookingURL, "_blank");
    } else {
      handleInquiryOpen(pkg);
    }
  };

  if (packages.length === 0) return null;

  return (
    <div className="mb-16 last:mb-0">
      {/* Resort Header */}
      <div className="flex flex-col items-center justify-center mb-8 border-b border-gray-100 pb-4">
        <h3 className="text-4xl md:text-5xl mb-4 font-sans font-bold text-gray-900 text-center">
          {resort.Title}
        </h3>
        {resort.TentCityName && (
          <div className="flex items-center gap-1.5 text-pink-600 mt-2">
            <MapPin className="w-4 h-4" />
            <span className="text-sm font-sans font-medium uppercase tracking-wider">
              {resort.TentCityName} Tent City
            </span>
          </div>
        )}
      </div>

      {/* Swiper Slider Wrapper */}
      <div className="relative px-0 sm:px-8">
        {packages.length > 1 && (
          <>
            <button
              ref={setPrevEl}
              className="hidden sm:flex absolute left-2 sm:-left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white rounded-full shadow-lg items-center justify-center hover:bg-pink-600 hover:text-white transition cursor-pointer border border-gray-100"
              aria-label="Previous slide"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              ref={setNextEl}
              className="hidden sm:flex absolute right-2 sm:-right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white rounded-full shadow-lg items-center justify-center hover:bg-pink-600 hover:text-white transition cursor-pointer border border-gray-100"
              aria-label="Next slide"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}
        <Swiper
          modules={[Autoplay, Pagination, Navigation]}
          spaceBetween={16}
          slidesPerView={1}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
          }}
          pagination={{
            el: paginationEl,
            clickable: true,
          }}
          navigation={{
            prevEl: prevEl,
            nextEl: nextEl,
          }}
          loop={packages.length > 3}
          breakpoints={{
            320: {
              slidesPerView: 1,
              spaceBetween: 12,
            },
            640: {
              slidesPerView: 2,
              spaceBetween: 20,
            },
            1024: {
              slidesPerView: packages?.length > 3 ? 3 : 2,
              spaceBetween: 28,
            },
            1280: {
              slidesPerView: packages?.length > 2 ? 3 : 2,
              spaceBetween: 32,
            },
          }}
          className="w-full py-4 px-1 -my-4"
        >
          {packages.map((pkg) => (
            <SwiperSlide key={pkg.ID} className="h-auto mb-2">
              <div className="group bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 flex flex-col h-full border border-gray-100">
                {/* Image */}
                <div className="relative h-56 md:h-64 overflow-hidden bg-gray-50">
                  <Link
                    href={`/package/${pkg.URL}`}
                    className="block w-full h-full"
                  >
                    <ImageWithFallback
                      src={
                        pkg.MainImage || pkg.BannerImage || "/placeholder.png"
                      }
                      alt={pkg.Title}
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 426px"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent"></div>
                  </Link>
                </div>

                {/* Content */}
                <div className="p-6 flex flex-col flex-grow">
                  <h4 className="text-lg md:text-xl mb-4 font-sans font-medium text-gray-800 group-hover:text-pink-600 transition duration-300 leading-snug min-h-[3rem]">
                    <Link href={`/package/${pkg.URL}`}>{pkg.Title}</Link>
                  </h4>

                  {/* Price */}
                  <div className="mt-auto pt-4 border-t border-gray-100 flex items-center justify-between mb-4">
                    <div className="flex items-end">
                      <span className="text-xl md:text-2xl font-medium text-gray-900">
                        ₹{pkg.Price ? pkg.Price.toLocaleString() : "0"}
                      </span>
                      <span className="text-sm text-gray-400 block ml-1">
                        {pkg?.PricePer ? `/ ${pkg.PricePer.replace(/^\/+/, "").trim()}` : "/ Per Person"}
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-2.5 mt-auto">
                    <button
                      onClick={() => handleBookNow(pkg)}
                      className="flex-1 bg-gradient-to-r from-pink-500 to-pink-600 text-white px-4 py-2.5 rounded-lg hover:shadow-lg transition text-sm cursor-pointer font-sans font-medium"
                    >
                      Book Now
                    </button>
                    <button
                      onClick={() => handleInquiryOpen(pkg)}
                      className="flex-1 text-center border-2 border-pink-500 text-pink-600 px-4 py-2.5 rounded-lg hover:bg-pink-50 transition text-sm cursor-pointer font-sans font-medium flex items-center justify-center animate-none"
                    >
                      Inquiry Now
                    </button>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Custom Pagination Container */}
        <div
          ref={setPaginationEl}
          className="custom-pagination-pkg flex justify-center gap-2 mt-6"
        />
      </div>

      <InquiryDialog
        isOpen={inquiryOpen}
        onClose={() => setInquiryOpen(false)}
        contextName={selectedPkg?.Title}
        contextURL={selectedPkg?.URL}
        inquiryType="Rann Packages"
      />
    </div>
  );
}

export default function ResortWisePackages({ cmsData }: { cmsData?: any }) {
  const { homeDetails } = useAppSelector((state) => state.home);
  const resorts: Resort[] = homeDetails?.Resort ?? [];

  // Filter resorts that contain at least one package
  const resortsWithPackages = resorts.filter(
    (resort) => resort.ResortWisePackage && resort.ResortWisePackage.length > 0,
  );

  if (resortsWithPackages.length === 0) return null;

  return (
    <section
      id="resort-packages"
      className="py-20 bg-white select-none relative"
    >
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header */}
        {/* {cmsData?.Template ? (
          <div
            dangerouslySetInnerHTML={{ __html: cmsData.Template }}
            className="text-center mb-16"
          />
        ) : (
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl mb-4 font-sans font-semibold text-gray-900">
              Resort-Wise{" "}
              <span className="bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 bg-clip-text text-transparent">
                Packages
              </span>
            </h2>
            <p className="text-xl text-gray-600 font-sans">
              Explore curated packages tailored to your preferred stay
            </p>
          </div>
        )} */}

        {/* Resorts List */}
        <div className="space-y-16">
          {resortsWithPackages.map((resort) => (
            <ResortSliderSection key={resort.ID} resort={resort} />
          ))}
        </div>
      </div>
    </section>
  );
}
